import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useAuthActions = () => {
  const { toast } = useToast();

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Login failed",
        description: error.message || "Please check your credentials and try again",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signUp = async (
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
        },
      });
      
      if (error) throw error;
      
      toast({
        title: "Registration successful",
        description: "Please check your email to confirm your account",
      });
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description: error.message || "Please try again",
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      
      // Don't show error for "session not found" - this is expected if session expired
      if (error && !error.message.includes('session_not_found')) {
        throw error;
      }
      
      // Clear all auth-related data from localStorage
      localStorage.removeItem('subscription');
      localStorage.clear(); // Clear everything to ensure clean slate
      
      toast({
        title: "Signed out successfully",
      });
      
      // Use window.location for navigation (works outside Router context and ensures full reload)
      setTimeout(() => {
        window.location.href = '/auth';
      }, 100);
      
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast({
        title: "Sign out failed",
        description: error.message,
        variant: "destructive",
      });
      
      // Even if sign-out fails on server, clear local state and redirect
      localStorage.clear();
      setTimeout(() => {
        window.location.href = '/auth';
      }, 100);
    }
  };

  return { signIn, signUp, signOut };
};
